import { useCallback, useEffect, useState } from 'react'
import { Button, Empty, Form, Input, Modal, Popconfirm, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { messageTemplatesApi, type MessageTemplate } from '../services/messageTemplatesApi'
import TgRichEditor from './TgRichEditor'
import TgMessagePreview from './TgMessagePreview'
import FormattedText from './FormattedText'
import { templateToTg, tgToPlain } from '../utils/tgFormat'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

export default function QuickTemplatesSettings() {
    const [enabled, setEnabled] = useState(false)
    const [templates, setTemplates] = useState<MessageTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [editing, setEditing] = useState<MessageTemplate | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [form] = Form.useForm()

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const [settingsResponse, templatesResponse] = await Promise.all([
                messageTemplatesApi.getSettings(),
                messageTemplatesApi.getTemplates(),
            ])
            setEnabled(settingsResponse.data.enabled)
            setTemplates(templatesResponse.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const toggleEnabled = async (value: boolean) => {
        setSaving(true)
        try {
            await messageTemplatesApi.updateSettings(value)
            setEnabled(value)
            message.success(value ? 'Шаблоны включены для всех' : 'Шаблоны скрыты у всех')
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const openModal = (template: MessageTemplate | null) => {
        setEditing(template)
        form.setFieldsValue({
            name: template?.name || '',
            category: template?.category || '',
            // В редакторе всегда разметка Telegram; старые шаблоны - простой текст.
            content: templateToTg(template?.content || '', template?.is_formatted),
        })
        setModalOpen(true)
    }

    const submit = async () => {
        const values = await form.validateFields()
        setSaving(true)
        try {
            const payload = { ...values, is_formatted: true }
            if (editing) {
                await messageTemplatesApi.updateTemplate(editing.id, payload)
            } else {
                await messageTemplatesApi.createTemplate(payload)
            }
            setModalOpen(false)
            setEditing(null)
            await load()
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const remove = async (template: MessageTemplate) => {
        try {
            await messageTemplatesApi.deleteTemplate(template.id)
            setTemplates((items) => items.filter((item) => item.id !== template.id))
        } catch (error) {
            message.error(extractError(error))
        }
    }

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space align="start" size={12}>
                    <Switch checked={enabled} loading={saving} onChange={toggleEnabled} />
                    <div>
                        <div>Показывать шаблоны над полем ввода</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Настройка общая: после включения панель появляется у всех операторов сразу.
                        </Text>
                    </div>
                </Space>

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text type="secondary">Шаблонов: {templates.length}</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>
                        Добавить шаблон
                    </Button>
                </Space>

                {templates.length === 0 ? (
                    <Empty description="Шаблонов пока нет" />
                ) : (
                    <Table
                        dataSource={templates}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        scroll={{ x: 720 }}
                        columns={[
                            {
                                title: 'Название',
                                dataIndex: 'name',
                                width: 220,
                                render: (value: string, row: MessageTemplate) => (
                                    <Space direction="vertical" size={2}>
                                        <Text strong>{value}</Text>
                                        {row.category && <Tag style={{ marginInlineEnd: 0 }}>{row.category}</Tag>}
                                    </Space>
                                ),
                            },
                            {
                                title: 'Текст',
                                dataIndex: 'content',
                                render: (value: string, row: MessageTemplate) => (
                                    <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                        {row.is_formatted ? <FormattedText html={value} /> : value}
                                    </Text>
                                ),
                            },
                            {
                                title: 'Применений',
                                dataIndex: 'usage_count',
                                width: 110,
                                align: 'center' as const,
                            },
                            {
                                title: '',
                                key: 'actions',
                                width: 90,
                                render: (_: unknown, row: MessageTemplate) => (
                                    <Space size={4}>
                                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openModal(row)} />
                                        <Popconfirm title="Удалить шаблон?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove(row)}>
                                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                )}
            </Space>

            <Modal
                open={modalOpen}
                title={editing ? 'Изменить шаблон' : 'Новый шаблон'}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={saving}
                onOk={submit}
                onCancel={() => { setModalOpen(false); setEditing(null) }}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
                        <Input placeholder="Например: Сумма изменена" maxLength={100} />
                    </Form.Item>
                    <Form.Item
                        name="category"
                        label="Категория"
                        extra="Шаблоны с одинаковой категорией группируются вместе. Можно оставить пустой."
                    >
                        <Input placeholder="Например: Изменение суммы" maxLength={50} />
                    </Form.Item>
                    <Form.Item
                        name="content"
                        label="Текст сообщения"
                        rules={[{
                            validator: (_, value: string) => (tgToPlain(value || '').trim()
                                ? Promise.resolve()
                                : Promise.reject(new Error('Введите текст'))),
                        }]}
                    >
                        <TgRichEditor minRows={5} maxRows={12} placeholder="Текст, который подставится в поле ввода" />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.content !== next.content}>
                        {() => <TgMessagePreview value={form.getFieldValue('content') || ''} withSamples={false} />}
                    </Form.Item>
                </Form>
            </Modal>
        </Spin>
    )
}
